import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Search } from 'lucide-react';
import type { Feedback } from '@/types/feedback';
import type { FeedbackFiltersState } from './FeedbackList';
import { voteSentiment, normalizeVote } from '@/lib/vote';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Sentiment = 'Positive' | 'Negative';
type View = 'feed' | 'byfeature' | 'board';
type Sort = 'newest' | 'oldest' | 'highest' | 'lowest';

interface Row {
  feature: string;
  company: string;
  user: string;
  text: string;
  stars: number;
  sentiment: Sentiment | null;
  ts: number;
  raw: Feedback;
}

// Sentiment follows the vote only: up = Positive, down = Negative. A row with no
// thumb has no sentiment (null) — no badge, and it sits outside the lanes.
function sentimentOf(vote: string): Sentiment | null {
  const s = voteSentiment(vote);
  return s === 'positive' ? 'Positive' : s === 'negative' ? 'Negative' : null;
}

function ago(ts: number): string {
  const m = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const BADGE: Record<Sentiment, string> = {
  Positive: 'bg-success-100 text-success-700',
  Negative: 'bg-error-100 text-error-700',
};
const BAR: Record<Sentiment, string> = {
  Positive: 'bg-success-500',
  Negative: 'bg-error-500',
};

// Purely visual sentiment label — colour only, not interactive.
function SentBadge({ s }: { s: Sentiment }) {
  return <span className={cn('rounded-md px-2 py-0.5 text-xs font-semibold', BADGE[s])}>{s}</span>;
}

function Stars({ n }: { n: number }) {
  const r = Math.max(0, Math.min(5, Math.round(n)));
  return (
    <span className="text-xs tracking-tight" aria-label={`${r} of 5 stars`}>
      <span className="text-warning-500">{'★'.repeat(r)}</span>
      <span className="text-muted-foreground/30">{'★'.repeat(5 - r)}</span>
    </span>
  );
}

/**
 * Rich per-account comments explorer: search / sort / sentiment + feature filter /
 * pagination / view grouping, all client-side over the account's feedback rows that
 * carry a written comment. Views: Feed, By feature, By sentiment.
 */
export function AccountComments({
  feedback,
  onOpen,
  filters,
}: {
  feedback: Feedback[];
  onOpen?: (f: Feedback) => void;
  /** The page's filter controls. All five narrow the comments list (AND logic). */
  filters: FeedbackFiltersState;
}) {
  // Sentiment (vote) → this component's Sentiment label. normalizeVote tolerates
  // casing / legacy values so the comparison can't silently miss; 'all' = inactive.
  const kind = normalizeVote(filters.vote);
  const wantedSentiment: Sentiment | null =
    kind === 'up' ? 'Positive' : kind === 'down' ? 'Negative' : null;

  // The four content filters, applied to every view. Empty string / 'all' = inactive.
  // Feature/Customer/User are chosen from the data's own facet values, so a
  // case-insensitive exact match is correct; Rating is numeric.
  const eqi = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
  const matchesContent = (x: Row): boolean =>
    (!filters.feature || eqi(x.raw.feature, filters.feature)) &&
    (!filters.customer || eqi(x.raw.customer, filters.customer)) &&
    (!filters.user || eqi(x.raw.user, filters.user)) &&
    (filters.rating === 'all' || x.stars === Number(filters.rating));
  const [view, setView] = useState<View>('feed');
  const [sort, setSort] = useState<Sort>('newest');
  const [q, setQ] = useState('');
  const [per, setPer] = useState(5);
  const [page, setPage] = useState(1);

  useEffect(
    () => setPage(1),
    [sort, q, per, view, wantedSentiment, filters.feature, filters.customer, filters.user, filters.rating]
  );

  const rows: Row[] = useMemo(
    () =>
      feedback
        .filter((f) => f.feedback && f.feedback.trim().length > 0)
        .map((f) => ({
          feature: f.feature || '—',
          company: f.customer || '—',
          user: f.user,
          text: f.feedback,
          stars: f.rating,
          sentiment: sentimentOf(f.vote),
          ts: new Date(f.timestamp).getTime() || 0,
          raw: f,
        })),
    [feedback]
  );

  const filtered = (): Row[] => {
    const query = q.trim().toLowerCase();
    const r = rows.filter(
      (x) => query === '' || `${x.text} ${x.feature} ${x.company} ${x.user}`.toLowerCase().includes(query)
    );
    return [...r].sort((a, b) =>
      sort === 'newest'
        ? b.ts - a.ts
        : sort === 'oldest'
          ? a.ts - b.ts
          : sort === 'highest'
            ? b.stars - a.stars || b.ts - a.ts
            : a.stars - b.stars || b.ts - a.ts
    );
  };

  // Search + sort, then the four content filters, then Sentiment. Every view maps
  // over this one fully-filtered array so all filters apply consistently. With
  // Sentiment = All the board shows both lanes; with Positive/Negative it narrows
  // to that lane (the other lane is empty), same as the feed.
  const base = filtered();
  const contentRows = base.filter(matchesContent);
  const feedRows = wantedSentiment ? contentRows.filter((x) => x.sentiment === wantedSentiment) : contentRows;
  const pages = Math.max(1, Math.ceil(feedRows.length / per));
  const curPage = Math.min(page, pages);
  const start = (curPage - 1) * per;
  const slice = feedRows.slice(start, start + per);

  const showing =
    view === 'feed'
      ? feedRows.length
        ? `Showing ${start + 1}–${Math.min(start + per, feedRows.length)} of ${feedRows.length} feedback items`
        : 'No matching items'
      : view === 'byfeature'
        ? `${feedRows.length} feedback items across ${new Set(feedRows.map((r) => r.feature)).size} feature(s)`
        : `${feedRows.length} feedback items across 2 sentiment lanes`;

  const exportCsv = () => {
    const head = ['feature', 'company', 'sentiment', 'stars', 'comment', 'user', 'posted'];
    const rowsCsv = feedRows.map((x) => [x.feature, x.company, x.sentiment ?? '', x.stars, x.text, x.user, ago(x.ts)]);
    const csv = [head, ...rowsCsv]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comments.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="account-comments">
      {/* Topbar */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Comments</h2>
          <p className="text-sm text-muted-foreground">{showing}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search comments"
              className="h-9 w-[220px] pl-8"
            />
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Sort">
          <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
            <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="highest">Highest rated</SelectItem>
              <SelectItem value="lowest">Lowest rated</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex-1" />
        <Field label="View">
          <div className="inline-flex overflow-hidden rounded-md border">
            {([['feed', 'Feed'], ['byfeature', 'By feature'], ['board', 'By sentiment']] as const).map(([v, label], i) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  'h-9 px-4 text-sm font-medium',
                  i > 0 && 'border-l',
                  view === v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>
      </div>

      {/* List */}
      {view === 'feed' && <FeedView rows={slice} onOpen={onOpen} />}
      {view === 'byfeature' && <FeatureView rows={feedRows} />}
      {view === 'board' && <BoardView rows={feedRows} onOpen={onOpen} />}

      {/* Pager (feed only) */}
      {view === 'feed' && feedRows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            Per page
            <Select value={String(per)} onValueChange={(v) => setPer(Number(v))}>
              <SelectTrigger className="h-8 w-[72px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[5, 10, 25].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Page {curPage} of {pages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(curPage - 1)} disabled={curPage <= 1} aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(curPage + 1)} disabled={curPage >= pages} aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function cardInteractive(onOpen: ((f: Feedback) => void) | undefined, raw: Feedback) {
  if (!onOpen) return {};
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onOpen(raw),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(raw);
      }
    },
  };
}

function FeedView({
  rows,
  onOpen,
}: {
  rows: Row[];
  onOpen?: (f: Feedback) => void;
}) {
  if (rows.length === 0) {
    return <div className="rounded-md border bg-muted/50 py-12 text-center text-muted-foreground">No comments match these filters.</div>;
  }
  return (
    <div className="space-y-3">
      {rows.map((x, i) => (
        <div
          key={i}
          {...cardInteractive(onOpen, x.raw)}
          className={cn('rounded-lg border bg-card p-4', onOpen && 'cursor-pointer transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
        >
          <div className="flex flex-wrap items-center gap-2">
            {x.sentiment && <SentBadge s={x.sentiment} />}
            <Stars n={x.stars} />
            <span className="font-medium">{x.feature}</span>
            <span className="text-muted-foreground cx-mask">· {x.company}</span>
          </div>
          <p className="my-2 text-sm leading-relaxed">{x.text}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="max-w-[220px] truncate cx-mask" title={x.user}>{x.user}</span>
            <span>·</span>
            <span>{ago(x.ts)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function FeatureView({ rows }: { rows: Row[] }) {
  const groups = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const r of rows) (map.get(r.feature) ?? map.set(r.feature, []).get(r.feature)!).push(r);
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [rows]);

  if (groups.length === 0) {
    return <div className="rounded-md border bg-muted/50 py-12 text-center text-muted-foreground">No comments match these filters.</div>;
  }

  return (
    <div className="max-h-[560px] space-y-3 overflow-y-auto pr-1">
      {groups.map(([name, g]) => {
        const total = g.length;
        const avg = g.reduce((a, r) => a + r.stars, 0) / total;
        const counts: Record<Sentiment, number> = {
          Positive: g.filter((r) => r.sentiment === 'Positive').length,
          Negative: g.filter((r) => r.sentiment === 'Negative').length,
        };
        const pct = (v: number) => `${Math.round((v / total) * 100)}%`;
        const companies = [...new Set(g.map((r) => r.company))];
        return (
          <div key={name} className="rounded-xl border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-base font-semibold">{name}</span>
                <span className="ml-2 text-sm text-muted-foreground">{total} comment{total > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <Stars n={avg} />
                <span className="font-semibold tabular-nums">{avg.toFixed(1)}</span>
              </div>
            </div>
            <div className="my-3 flex h-2 overflow-hidden rounded-full bg-muted">
              {(['Positive', 'Negative'] as Sentiment[]).map((s) =>
                counts[s] ? <span key={s} className={BAR[s]} style={{ width: pct(counts[s]) }} /> : null
              )}
            </div>
            <div className="mb-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-middle', BAR.Positive)} />{counts.Positive} positive</span>
              <span><i className={cn('mr-1.5 inline-block h-2 w-2 rounded-sm align-middle', BAR.Negative)} />{counts.Negative} negative</span>
            </div>
            <div className="mb-1 flex flex-wrap gap-1.5">
              {companies.slice(0, 8).map((c) => (
                <span key={c} className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground cx-mask">{c}</span>
              ))}
            </div>
            {g.slice(0, 3).map((x, i) => (
              <div key={i} className="mt-2 border-t pt-2 text-sm">
                {x.text}
                <span className="ml-1.5 text-xs text-muted-foreground cx-mask">{x.company} · {ago(x.ts)}</span>
              </div>
            ))}
            {total > 3 && <div className="mt-2 border-t pt-2 text-sm text-muted-foreground">+ {total - 3} more</div>}
          </div>
        );
      })}
    </div>
  );
}

function BoardView({ rows, onOpen }: { rows: Row[]; onOpen?: (f: Feedback) => void }) {
  const cols: Sentiment[] = ['Positive', 'Negative'];
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {cols.map((s) => {
        const g = rows.filter((r) => r.sentiment === s);
        return (
          <div key={s} className="rounded-xl border bg-muted/40 p-3">
            <div className={cn('mb-3 flex items-center justify-between border-b-2 px-1 pb-2',
              s === 'Positive' ? 'border-success-500' : 'border-error-500')}>
              <span className="flex items-center gap-2 font-semibold">
                <i className={cn('inline-block h-2 w-2 rounded-sm', BAR[s])} />
                {s}
              </span>
              <span className="rounded-full border bg-background px-2 py-0.5 text-xs text-muted-foreground">{g.length}</span>
            </div>
            {g.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">Nothing here</div>
            ) : (
              <div className="max-h-[496px] overflow-y-auto pr-1">
                {g.map((x, i) => (
                  <div
                    key={i}
                    {...cardInteractive(onOpen, x.raw)}
                    className={cn('mb-2.5 rounded-lg border bg-card p-3', onOpen && 'cursor-pointer transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
                  >
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <Stars n={x.stars} />
                      <span className="text-sm font-medium">{x.feature}</span>
                      <span className="text-xs text-muted-foreground cx-mask">· {x.company}</span>
                    </div>
                    <div className="text-sm">{x.text}</div>
                    <div className="mt-2 text-xs text-muted-foreground cx-mask">{x.user} · {ago(x.ts)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
